import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Box, Tab, Tabs } from "@mui/material";

import "./UITabs.scss";

const UITabs = ({
  tabs,
  value,
  onChange,
  children,
  classNames,
  className,
  rootSx,
  variant,
  scrollButtons,
  allowScrollButtonsMobile,
  ...tabProps
}) => {
  const tabsClassName = ["ui-tabs", className].filter(Boolean).join(" ");
  const rootRef = useRef(null);

  // Desktop: translate a vertical mouse-wheel into horizontal scrolling of the
  // tab strip so overflowing tabs stay reachable without scroll buttons.
  useEffect(() => {
    if (variant !== "scrollable") return undefined;
    const scroller = rootRef.current?.querySelector(".MuiTabs-scroller");
    if (!scroller) return undefined;

    const handleWheel = (event) => {
      if (event.deltaY === 0) return;
      if (scroller.scrollWidth <= scroller.clientWidth) return;
      event.preventDefault();
      scroller.scrollLeft += event.deltaY;
    };

    scroller.addEventListener("wheel", handleWheel, { passive: false });
    return () => scroller.removeEventListener("wheel", handleWheel);
  }, [variant, tabs.length]);

  return (
    <Box className={classNames.root} sx={rootSx} ref={rootRef}>
      <Tabs
        className={tabsClassName}
        value={value}
        onChange={(_event, next) => onChange?.(next)}
        variant={variant}
        scrollButtons={scrollButtons}
        allowScrollButtonsMobile={allowScrollButtonsMobile}
        {...tabProps}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={tab.label}
            disabled={tab.disabled}
          />
        ))}
      </Tabs>
      {children}
    </Box>
  );
};

UITabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      disabled: PropTypes.bool,
    }),
  ).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func,
  children: PropTypes.node,
  classNames: PropTypes.object,
  className: PropTypes.string,
  rootSx: PropTypes.object,
  variant: PropTypes.oneOf(["standard", "scrollable", "fullWidth"]),
  scrollButtons: PropTypes.oneOfType([
    PropTypes.oneOf(["auto", true, false]),
    PropTypes.bool,
  ]),
  allowScrollButtonsMobile: PropTypes.bool,
};

UITabs.defaultProps = {
  onChange: undefined,
  children: null,
  classNames: {},
  className: "",
  rootSx: {},
  variant: "scrollable",
  scrollButtons: false,
  allowScrollButtonsMobile: true,
};

export default UITabs;
